import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    DeleteSweep as EraseIcon,
} from '@mui/icons-material';

const CHUNK_BYTES = 90; // LOG_DATA carries up to 90 bytes per packet
const STALL_TIMEOUT_MS = 2000;

const formatBytes = (n) => {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const formatTime = (utcSeconds) => {
    if (!utcSeconds) return '—';
    try { return new Date(utcSeconds * 1000).toLocaleString(); } catch { return '—'; }
};

const triggerDownload = (bytes, name) => {
    const blob = new Blob([bytes], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const Logs = ({ selectedMavId, onLogProgress, registerLogCancel, pushTransferHistory }) => {
    const [entries, setEntries] = useState({});
    const [numLogs, setNumLogs] = useState(0);
    const [listing, setListing] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [eraseOpen, setEraseOpen] = useState(false);

    const dlRef = useRef(null);
    const tickRef = useRef(null);

    const reportProgress = (p) => {
        if (typeof onLogProgress === 'function') onLogProgress(p);
    };

    const computeStats = () => {
        const dl = dlRef.current;
        if (!dl) return null;
        const now = performance.now();
        while (dl.samples.length > 1 && now - dl.samples[0].t > 3000) dl.samples.shift();
        const head = dl.samples[0];
        const tail = dl.samples[dl.samples.length - 1] || { t: now, bytes: dl.received };
        const dt = (tail.t - head.t) / 1000;
        const rate = dt > 0 ? (tail.bytes - head.bytes) / dt : 0; // B/s
        const elapsed = (now - dl.startedAt) / 1000;
        const avgRate = elapsed > 0 ? dl.received / elapsed : 0;
        const eta = rate > 0 && dl.size > dl.received ? (dl.size - dl.received) / rate : NaN;
        return { id: dl.id, size: dl.size, received: dl.received, rate, avgRate, eta };
    };

    const finishDownload = (commit) => {
        const dl = dlRef.current;
        if (!dl) return;
        if (dl.timer) { clearTimeout(dl.timer); dl.timer = null; }
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
        try { window.api.logRequestEnd(selectedMavId); } catch (e) { /* ignore */ }
        dlRef.current = null;
        setIsDownloading(false);
        reportProgress(null);
        if (typeof registerLogCancel === 'function') registerLogCancel(null);
        if (typeof pushTransferHistory === 'function') {
            pushTransferHistory({
                kind: 'download',
                name: `log_${dl.id}.bin`,
                path: `log_${dl.id}.bin`,
                size: dl.received,
                status: commit ? 'completed' : 'cancelled',
            });
        }
        if (commit && dl.bytes) {
            triggerDownload(dl.bytes.slice(0, dl.received), `log_${dl.id}.bin`);
        }
    };

    const armStallTimer = () => {
        const dl = dlRef.current;
        if (!dl) return;
        if (dl.timer) clearTimeout(dl.timer);
        dl.timer = setTimeout(() => {
            if (!dlRef.current) return;
            const remaining = Math.max(0, dl.size - dl.received);
            if (remaining === 0) { finishDownload(true); return; }
            try {
                window.api.logRequestData(selectedMavId, dl.id, dl.received, remaining);
            } catch (e) { /* ignore */ }
            armStallTimer();
        }, STALL_TIMEOUT_MS);
    };

    useEffect(() => {
        const onEntry = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            if (d.num_logs === 0) {
                setEntries({});
                setNumLogs(0);
                setListing(false);
                return;
            }
            setEntries((prev) => ({ ...prev, [d.id]: { id: d.id, size: d.size, time_utc: d.time_utc } }));
            setNumLogs(d.num_logs);
            if (d.id === d.last_log_num) setListing(false);
        };
        const onData = (e) => {
            const d = e.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            const dl = dlRef.current;
            if (!dl || d.id !== dl.id) return;
            const ofs = d.ofs >>> 0;
            const count = d.count & 0xff;
            const raw = d.data || '';
            const end = ofs + count;
            if (end > dl.bytes.length) {
                const grown = new Uint8Array(Math.max(dl.bytes.length * 2, end));
                grown.set(dl.bytes);
                dl.bytes = grown;
            }
            for (let i = 0; i < count; i++) {
                dl.bytes[ofs + i] = raw.charCodeAt(i) & 0xff;
            }
            if (end > dl.received) dl.received = end;
            dl.samples.push({ t: performance.now(), bytes: dl.received });
            armStallTimer();
            if (count < CHUNK_BYTES || dl.received >= dl.size) {
                finishDownload(true);
            }
        };
        window.addEventListener('LOG_ENTRY', onEntry);
        window.addEventListener('LOG_DATA', onData);
        return () => {
            window.removeEventListener('LOG_ENTRY', onEntry);
            window.removeEventListener('LOG_DATA', onData);
        };
    }, [selectedMavId]);

    useEffect(() => () => {
        if (dlRef.current) finishDownload(false);
    }, []);

    const refreshList = () => {
        if (selectedMavId == null) return;
        setEntries({});
        setNumLogs(0);
        setListing(true);
        window.api.logRequestList(selectedMavId, 0, 0xFFFF);
    };

    const startDownload = (entry) => {
        if (selectedMavId == null) return;
        if (dlRef.current) finishDownload(false);
        const size = entry.size >>> 0;
        const now = performance.now();
        dlRef.current = {
            id: entry.id,
            size,
            bytes: new Uint8Array(size || 1024),
            received: 0,
            timer: null,
            startedAt: now,
            samples: [{ t: now, bytes: 0 }],
        };
        setIsDownloading(true);
        reportProgress({ id: entry.id, size, received: 0, rate: 0, avgRate: 0, eta: NaN });
        if (typeof registerLogCancel === 'function') {
            registerLogCancel(() => finishDownload(false));
        }
        window.api.logRequestData(selectedMavId, entry.id, 0, size || 0xFFFFFFFF);
        armStallTimer();
        if (tickRef.current) clearInterval(tickRef.current);
        tickRef.current = setInterval(() => {
            const s = computeStats();
            if (s) reportProgress(s);
        }, 250);
    };

    const eraseAll = () => {
        window.api.logErase(selectedMavId);
        setEraseOpen(false);
        setEntries({});
        setNumLogs(0);
    };

    const rows = Object.values(entries).sort((a, b) => a.id - b.id);
    const disabled = selectedMavId == null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'action.hover' }}
            >
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Legacy Logs</Typography>
                    {numLogs > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {`${rows.length} / ${numLogs}`}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title={listing ? 'Listing…' : 'Refresh list'}>
                        <span>
                            <IconButton
                                onClick={refreshList}
                                disabled={disabled || listing}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Erase all logs">
                        <span>
                            <IconButton
                                onClick={() => setEraseOpen(true)}
                                disabled={disabled}
                                color="error"
                            >
                                <EraseIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 60 }}>ID</TableCell>
                            <TableCell>UTC Time</TableCell>
                            <TableCell align="right">Size</TableCell>
                            <TableCell align="right" sx={{ width: 80 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                    {listing ? 'Listing…' : 'No entries'}
                                </TableCell>
                            </TableRow>
                        ) : rows.map((r) => (
                            <TableRow key={r.id} hover>
                                <TableCell>{r.id}</TableCell>
                                <TableCell>{formatTime(r.time_utc)}</TableCell>
                                <TableCell align="right">{formatBytes(r.size)}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Download">
                                        <span>
                                            <IconButton
                                                onClick={() => startDownload(r)}
                                                disabled={disabled || isDownloading}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={eraseOpen} onClose={() => setEraseOpen(false)}>
                <DialogTitle>Erase All Logs?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will permanently erase all dataflash logs on the flight controller. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEraseOpen(false)}>Cancel</Button>
                    <Button onClick={eraseAll} color="error" variant="contained">Erase</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Logs;
